import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <LegalHeader
        eyebrow="Legal"
        title="Privacy Policy"
        updated="February 1, 2026"
      />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="privacy-page">
        <div className="prose prose-slate max-w-none">
          <Intro>
            SELLEXR ("we", "us", "our") operates the SELLEXR platform that connects local shopkeepers with certified freelancers for e-commerce operations on Amazon, Flipkart, Meesho and other marketplaces. This Privacy Policy explains what information we collect, why we collect it, and how we handle it.
          </Intro>

          <Section title="1. Information We Collect">
            <p>We collect the following categories of information when you use SELLEXR:</p>
            <ul>
              <li><strong>Account information:</strong> name, email address, phone number, city, role (shopkeeper / freelancer / admin), and password (stored as an irreversible bcrypt hash).</li>
              <li><strong>Profile information:</strong> store name, marketplace memberships, skills, bio, avatar color, ratings, and reviews.</li>
              <li><strong>Business data:</strong> tasks you create or complete, inventory items, payment records, and assignments between shopkeepers and freelancers.</li>
              <li><strong>Communications:</strong> messages sent through the contact form, notifications, and support requests.</li>
              <li><strong>Technical data:</strong> IP address, browser type, device information, session tokens, and application logs.</li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <ul>
              <li>To provide, maintain, and improve the SELLEXR platform.</li>
              <li>To authenticate users and prevent fraud or unauthorised access.</li>
              <li>To match shopkeepers with the most suitable freelancers using our AI matching system.</li>
              <li>To send transactional emails (verification codes, password resets, notifications).</li>
              <li>To generate certificates and track training progress in the SELLEXR Academy.</li>
              <li>To calculate ratings, earnings, and platform analytics.</li>
              <li>To comply with legal obligations and respond to lawful requests.</li>
            </ul>
          </Section>

          <Section title="3. Legal Basis for Processing">
            <p>We process your personal information under one or more of the following legal bases: your consent, the performance of a contract with you, our legitimate interests in operating the platform, and compliance with legal obligations under applicable Indian law and other jurisdictions where you reside.</p>
          </Section>

          <Section title="4. Sharing of Information">
            <p>We do <strong>not</strong> sell your personal information. We share limited information only with:</p>
            <ul>
              <li><strong>Shopkeepers and freelancers</strong> — to enable hiring, task assignment, ratings, and payment records between matched parties.</li>
              <li><strong>Service providers</strong> — hosting, email delivery (Resend), and AI matching (Claude via Emergent Integrations) that support our operations under strict confidentiality obligations.</li>
              <li><strong>Legal authorities</strong> — when required by law, court order, or to protect rights, property, or safety.</li>
            </ul>
          </Section>

          <Section title="5. Data Retention">
            <p>We retain personal information for as long as your account is active and for a reasonable period thereafter to comply with legal obligations, resolve disputes, and enforce agreements. You may request deletion of your account at any time by contacting <a href="mailto:care@sellexr.com" className="text-brand">care@sellexr.com</a>.</p>
          </Section>

          <Section title="6. Security">
            <p>We use industry-standard measures to protect your data, including TLS in transit, bcrypt-hashed passwords, JWT-based session tokens, role-based access control, and hardened cloud infrastructure. However, no method of transmission or storage is 100% secure.</p>
          </Section>

          <Section title="7. Cookies and Local Storage">
            <p>SELLEXR uses browser local storage to keep you signed in (authentication token). We do not use third-party advertising cookies. You may clear this storage at any time from your browser settings.</p>
          </Section>

          <Section title="8. Your Rights">
            <p>Depending on your jurisdiction, you may have rights to access, correct, delete, restrict, or export your personal data, and to withdraw consent. To exercise any right, email <a href="mailto:care@sellexr.com" className="text-brand">care@sellexr.com</a>.</p>
          </Section>

          <Section title="9. Children">
            <p>SELLEXR is not intended for individuals under 18. We do not knowingly collect personal information from minors. If you believe a minor has provided us information, please contact us and we will delete it.</p>
          </Section>

          <Section title="10. International Transfers">
            <p>Your information may be processed on servers located outside your country. Where required, we implement contractual safeguards for cross-border transfers.</p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will post the updated version on this page and, when appropriate, notify you via email or in-app notification.</p>
          </Section>

          <Section title="12. Contact Us">
            <p>For any questions about this Privacy Policy or our data practices, contact:</p>
            <p className="not-prose">
              <strong>SELLEXR</strong><br />
              Email: <a href="mailto:care@sellexr.com" className="text-brand">care@sellexr.com</a><br />
              Support: <a href="mailto:hello@sellexr.com" className="text-brand">hello@sellexr.com</a>
            </p>
          </Section>

          <div className="mt-12 pt-8 border-t border-slate-200 text-sm text-slate-500">
            See also our <Link to="/terms" className="text-brand font-medium">Terms &amp; Conditions</Link>.
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function LegalHeader({ eyebrow, title, updated }) {
  return (
    <section className="border-b border-slate-200 bg-slate-50/60">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <div className="eyebrow">{eyebrow}</div>
        <h1 className="mt-3 font-heading font-bold text-4xl lg:text-5xl text-[#0F172A]">{title}</h1>
        <p className="mt-3 text-sm text-slate-500">Last updated: {updated}</p>
      </div>
    </section>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-heading font-bold text-xl text-[#0F172A] mb-3">{title}</h2>
      <div className="text-slate-700 leading-relaxed space-y-3 text-[15px]">{children}</div>
    </section>
  );
}

function Intro({ children }) {
  return <p className="text-lg text-slate-700 leading-relaxed">{children}</p>;
}

export { LegalHeader, Section, Intro };
